import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MailIcon, LockIcon, EyeIcon, EyeOffIcon, CheckCircleIcon, ArrowLeftIcon, SparklesIcon, ZapIcon, Share2Icon, ShieldCheckIcon } from 'lucide-react';
type AuthView = 'login' | 'signup' | 'reset';
interface FormErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
  terms?: string;
}
const viewFromPath = (pathname: string): AuthView => {
  if (pathname === '/signup') return 'signup';
  if (pathname === '/reset-password') return 'reset';
  return 'login';
};
export const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [view, setView] = useState<AuthView>(viewFromPath(location.pathname));
  useEffect(() => {
    setView(viewFromPath(location.pathname));
  }, [location.pathname]);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const clearForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setRememberMe(false);
    setAgreeTerms(false);
    setErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
    setResetSent(false);
  };
  const switchView = (newView: AuthView) => {
    clearForm();
    const pathMap: Record<AuthView, string> = {
      login: '/login',
      signup: '/signup',
      reset: '/reset-password'
    };
    navigate(pathMap[newView], {
      replace: true
    });
  };
  const validateEmail = (value: string): string | undefined => {
    if (!value.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Enter a valid email address';
    return undefined;
  };
  const validatePassword = (value: string): string | undefined => {
    if (!value) return 'Password is required';
    if (value.length < 8) return 'Password must be at least 8 characters';
    return undefined;
  };
  const handleLogin = () => {
    const newErrors: FormErrors = {};
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);
    if (emailError) newErrors.email = emailError;
    if (passwordError) newErrors.password = passwordError;
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      navigate('/');
    }, 1500);
  };
  const handleSignup = () => {
    const newErrors: FormErrors = {};
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);
    if (emailError) newErrors.email = emailError;
    if (passwordError) newErrors.password = passwordError;
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!agreeTerms) newErrors.terms = 'You must agree to the Terms & Conditions';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      navigate('/');
    }, 1500);
  };
  const handleReset = () => {
    const newErrors: FormErrors = {};
    const emailError = validateEmail(email);
    if (emailError) newErrors.email = emailError;
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setResetSent(true);
    }, 1500);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (view === 'login') handleLogin();else if (view === 'signup') handleSignup();else handleReset();
  };
  return <div className="min-h-screen flex bg-white" data-id="element-1395">
      {/* Left Branding Panel */}
      <div className="hidden md:flex md:w-[480px] lg:w-[520px] flex-shrink-0 bg-gradient-to-br from-indigo-600 via-blue-600 to-indigo-700 relative overflow-hidden" data-id="element-1396">
        {/* Decorative circles */}
        <div className="absolute top-[-120px] right-[-80px] w-[300px] h-[300px] rounded-full bg-white/5" data-id="element-1397" />
        <div className="absolute bottom-[-60px] left-[-40px] w-[200px] h-[200px] rounded-full bg-white/5" data-id="element-1398" />
        <div className="absolute top-[40%] right-[10%] w-[100px] h-[100px] rounded-full bg-white/[0.03]" data-id="element-1399" />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full" data-id="element-1400">
          {/* Logo */}
          <div data-id="element-1401">
            <button onClick={() => navigate('/')} className="flex items-center space-x-2.5 group" data-id="element-1402">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/niZcL1bi9DypZftp1zanDt/RealUseIcon.svg" alt="RealUseAI" className="h-9 w-9 brightness-0 invert" data-id="element-1403" />
              <span className="text-2xl font-bold text-white" data-id="element-1404">RealUseAI</span>
            </button>
          </div>

          {/* Tagline & Features */}
          <div className="space-y-8" data-id="element-1405">
            <div data-id="element-1406">
              <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight mb-4" data-id="element-1407">
                Turn your ideas into
                <br data-id="element-1408" />
                AI-powered apps
              </h2>
              <p className="text-indigo-200 text-lg" data-id="element-1409">
                No code required. Create, share, and discover practical AI tools
                in minutes.
              </p>
            </div>

            <div className="space-y-4" data-id="element-1410">
              <div className="flex items-start space-x-3" data-id="element-1411">
                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center mt-0.5" data-id="element-1412">
                  <SparklesIcon className="h-5 w-5 text-indigo-200" data-id="element-1413" />
                </div>
                <div data-id="element-1414">
                  <p className="text-white font-medium" data-id="element-1415">Build in minutes</p>
                  <p className="text-indigo-300 text-sm" data-id="element-1416">
                    Describe your idea and watch it come to life
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3" data-id="element-1417">
                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center mt-0.5" data-id="element-1418">
                  <Share2Icon className="h-5 w-5 text-indigo-200" data-id="element-1419" />
                </div>
                <div data-id="element-1420">
                  <p className="text-white font-medium" data-id="element-1421">Share instantly</p>
                  <p className="text-indigo-300 text-sm" data-id="element-1422">
                    Publish to the store or share with a link
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3" data-id="element-1423">
                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center mt-0.5" data-id="element-1424">
                  <ZapIcon className="h-5 w-5 text-indigo-200" data-id="element-1425" />
                </div>
                <div data-id="element-1426">
                  <p className="text-white font-medium" data-id="element-1427">Powered by AI</p>
                  <p className="text-indigo-300 text-sm" data-id="element-1428">
                    Access cutting-edge models with zero setup
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <p className="text-indigo-300 text-sm" data-id="element-1429">
            © 2026 RealUseAI. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right Form Panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12" data-id="element-1430">
        <div className="w-full max-w-[420px]" data-id="element-1431">
          {/* Mobile logo */}
          <div className="md:hidden flex items-center justify-center mb-10" data-id="element-1432">
            <button onClick={() => navigate('/')} className="flex items-center space-x-2" data-id="element-1433">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/niZcL1bi9DypZftp1zanDt/RealUseIcon.svg" alt="RealUseAI" className="h-8 w-8" data-id="element-1434" />
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent" data-id="element-1435">
                RealUseAI
              </span>
            </button>
          </div>

          {/* Reset Password Success State */}
          {view === 'reset' && resetSent ? <div className="text-center" data-id="element-1436">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1437">
                <CheckCircleIcon className="h-8 w-8 text-green-600" data-id="element-1438" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1439">
                Check your email
              </h1>
              <p className="text-gray-600 mb-8" data-id="element-1440">
                We've sent a password reset link to{' '}
                <span className="font-medium text-gray-900" data-id="element-1441">{email}</span>. It
                may take a few minutes to arrive.
              </p>
              <button onClick={() => switchView('login')} className="inline-flex items-center text-indigo-600 font-medium hover:text-indigo-700 transition-colors" data-id="element-1442">
                <ArrowLeftIcon className="h-4 w-4 mr-1.5" data-id="element-1443" />
                Back to sign in
              </button>
            </div> : <>
              {/* Header */}
              <div className="mb-8" data-id="element-1444">
                {view === 'reset' && <button onClick={() => switchView('login')} className="inline-flex items-center text-gray-500 hover:text-gray-700 text-sm mb-6 transition-colors" data-id="element-1445">
                    <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-1446" />
                    Back to sign in
                  </button>}
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-1447">
                  {view === 'login' && 'Welcome back'}
                  {view === 'signup' && 'Create your account'}
                  {view === 'reset' && 'Reset your password'}
                </h1>
                <p className="text-gray-500 mt-1.5" data-id="element-1448">
                  {view === 'login' && 'Sign in to continue building with AI'}
                  {view === 'signup' && 'Start creating AI apps in minutes'}
                  {view === 'reset' && "Enter your email and we'll send you a reset link"}
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} noValidate className="space-y-5" data-id="element-1449">
                {/* Email */}
                <div data-id="element-1450">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1451">
                    Email address
                  </label>
                  <div className="relative" data-id="element-1452">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none" data-id="element-1453">
                      <MailIcon className="h-5 w-5 text-gray-400" data-id="element-1454" />
                    </div>
                    <input id="email" type="email" autoComplete="email" value={email} onChange={e => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors(prev => ({
                    ...prev,
                    email: undefined
                  }));
                }} placeholder="you@example.com" className={`w-full rounded-lg border ${errors.email ? 'border-red-400 focus:ring-red-500' : 'border-gray-300 focus:ring-indigo-500'} py-3 pl-11 pr-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow`} data-id="element-1455" />
                  </div>
                  {errors.email && <p className="text-sm text-red-500 mt-1" data-id="element-1456">{errors.email}</p>}
                </div>

                {/* Password (login & signup) */}
                {view !== 'reset' && <div data-id="element-1457">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1458">
                      Password
                    </label>
                    <div className="relative" data-id="element-1459">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none" data-id="element-1460">
                        <LockIcon className="h-5 w-5 text-gray-400" data-id="element-1461" />
                      </div>
                      <input id="password" type={showPassword ? 'text' : 'password'} autoComplete={view === 'login' ? 'current-password' : 'new-password'} value={password} onChange={e => {
                  setPassword(e.target.value);
                  if (errors.password) setErrors(prev => ({
                    ...prev,
                    password: undefined
                  }));
                }} placeholder="••••••••" className={`w-full rounded-lg border ${errors.password ? 'border-red-400 focus:ring-red-500' : 'border-gray-300 focus:ring-indigo-500'} py-3 pl-11 pr-12 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow`} data-id="element-1462" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600 transition-colors" aria-label={showPassword ? 'Hide password' : 'Show password'} data-id="element-1463">
                        {showPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-1464" /> : <EyeIcon className="h-5 w-5" data-id="element-1465" />}
                      </button>
                    </div>
                    {errors.password && <p className="text-sm text-red-500 mt-1" data-id="element-1466">
                        {errors.password}
                      </p>}
                  </div>}

                {/* Confirm Password (signup only) */}
                {view === 'signup' && <div data-id="element-1467">
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1468">
                      Confirm password
                    </label>
                    <div className="relative" data-id="element-1469">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none" data-id="element-1470">
                        <ShieldCheckIcon className="h-5 w-5 text-gray-400" data-id="element-1471" />
                      </div>
                      <input id="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} autoComplete="new-password" value={confirmPassword} onChange={e => {
                  setConfirmPassword(e.target.value);
                  if (errors.confirmPassword) setErrors(prev => ({
                    ...prev,
                    confirmPassword: undefined
                  }));
                }} placeholder="••••••••" className={`w-full rounded-lg border ${errors.confirmPassword ? 'border-red-400 focus:ring-red-500' : 'border-gray-300 focus:ring-indigo-500'} py-3 pl-11 pr-12 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow`} data-id="element-1472" />
                      <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600 transition-colors" aria-label={showConfirmPassword ? 'Hide password' : 'Show password'} data-id="element-1473">
                        {showConfirmPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-1474" /> : <EyeIcon className="h-5 w-5" data-id="element-1475" />}
                      </button>
                    </div>
                    {errors.confirmPassword && <p className="text-sm text-red-500 mt-1" data-id="element-1476">
                        {errors.confirmPassword}
                      </p>}
                  </div>}

                {/* Remember me / Forgot password (login) */}
                {view === 'login' && <div className="flex items-center justify-between" data-id="element-1477">
                    <label className="flex items-center cursor-pointer" data-id="element-1478">
                      <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer" data-id="element-1479" />
                      <span className="ml-2 text-sm text-gray-600" data-id="element-1480">
                        Remember me
                      </span>
                    </label>
                    <button type="button" onClick={() => switchView('reset')} className="text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors" data-id="element-1481">
                      Forgot password?
                    </button>
                  </div>}

                {/* Terms checkbox (signup) */}
                {view === 'signup' && <div data-id="element-1482">
                    <label className="flex items-start cursor-pointer" data-id="element-1483">
                      <input type="checkbox" checked={agreeTerms} onChange={e => {
                  setAgreeTerms(e.target.checked);
                  if (errors.terms) setErrors(prev => ({
                    ...prev,
                    terms: undefined
                  }));
                }} className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 mt-0.5 cursor-pointer" data-id="element-1484" />
                      <span className="ml-2.5 text-sm text-gray-600 leading-snug" data-id="element-1485">
                        I agree to the{' '}
                        <button type="button" className="text-indigo-600 hover:text-indigo-700 font-medium underline underline-offset-2" data-id="element-1486">
                          Terms &amp; Conditions
                        </button>{' '}
                        and{' '}
                        <button type="button" className="text-indigo-600 hover:text-indigo-700 font-medium underline underline-offset-2" data-id="element-1487">
                          Privacy Policy
                        </button>
                      </span>
                    </label>
                    {errors.terms && <p className="text-sm text-red-500 mt-1" data-id="element-1488">
                        {errors.terms}
                      </p>}
                  </div>}

                {/* Submit Button */}
                <button type="submit" disabled={isLoading} className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-indigo-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center" data-id="element-1489">
                  {isLoading ? <div className="flex items-center space-x-2" data-id="element-1490">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1491" />
                      <span data-id="element-1492">
                        {view === 'login' && 'Signing in…'}
                        {view === 'signup' && 'Creating account…'}
                        {view === 'reset' && 'Sending link…'}
                      </span>
                    </div> : <>
                      {view === 'login' && 'Sign In'}
                      {view === 'signup' && 'Create Account'}
                      {view === 'reset' && 'Send Reset Link'}
                    </>}
                </button>
              </form>

              {/* Footer links */}
              <div className="mt-8 text-center" data-id="element-1493">
                {view === 'login' && <p className="text-sm text-gray-500" data-id="element-1494">
                    Don't have an account?{' '}
                    <button onClick={() => switchView('signup')} className="font-medium text-indigo-600 hover:text-indigo-700 transition-colors" data-id="element-1495">
                      Sign up
                    </button>
                  </p>}
                {view === 'signup' && <p className="text-sm text-gray-500" data-id="element-1496">
                    Already have an account?{' '}
                    <button onClick={() => switchView('login')} className="font-medium text-indigo-600 hover:text-indigo-700 transition-colors" data-id="element-1497">
                      Sign in
                    </button>
                  </p>}
              </div>
            </>}
        </div>
      </div>
    </div>;
};